/**
 * 成本看板的时间区间与下钻 —— 纯逻辑，无 Vue、无网络，可单测。
 *
 * **区间全程按 UTC 天。** 后端分桶用的是 `DATE_FORMAT(created_at, '%Y-%m-%d')`，而连接会话时区固定
 * UTC（`packages/db/src/create-db.js` 的 `connection.timezone = 'Z'`）—— 所以图上那一根柱子代表的
 * 是一个 UTC 天。筛选栏若按浏览器本地时区取边界，柱子和筛选就会错开半天，而且没人看得出来。
 *
 * 界面上选的是**日期**（`YYYY-MM-DD`），接口要的是 ISO 瞬时且区间半开 `[since, until)` ——
 * 所以「截止日」要 +1 天才能把当天算进去。这个 +1 只在这里发生一次。
 */

/** 一天的毫秒数。 */
const DAY_MS = 86_400_000

/** `YYYY-MM-DD`。 */
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/**
 * 选中的日期区间（含首尾两天）→ 接口要的半开 ISO 区间。
 * @param {{ sinceDay: string, untilDay: string }} days
 * @returns {{ since: string, until: string }}
 */
export function isoRangeOf({ sinceDay, untilDay }) {
  assertDay(sinceDay)
  assertDay(untilDay)
  if (sinceDay > untilDay) {
    throw new Error(`cost-range: 起始日必须不晚于截止日（收到 ${sinceDay} → ${untilDay}）`)
  }
  return { since: startOfDay(sinceDay), until: startOfDay(nextDayOf(untilDay)) }
}

/**
 * 快捷区间：截止到 `today`（含），往前数 `days` 天。
 * @param {string} today  `YYYY-MM-DD`，UTC 今天
 * @param {number} days   1 = 只看今天
 * @returns {{ sinceDay: string, untilDay: string }}
 */
export function recentDaysOf(today, days) {
  assertDay(today)
  if (!Number.isInteger(days) || days < 1) {
    throw new Error(`cost-range: days 必须是正整数（收到 ${days}）`)
  }
  return { sinceDay: dayOffsetOf(today, -(days - 1)), untilDay: today }
}

/** 浏览器此刻的 UTC 日期。 @param {Date} now */
export function utcDayOf(now) {
  return now.toISOString().slice(0, 10)
}

/**
 * 下钻到调用日志：把当前筛选条件原样带过去（`/llm/calls` 的 query 参数名与后端
 * 统一调用日志的 query schema 一致：`capability` / `provider` / `model` / `user_id` / `since` / `until`）。
 *
 * @param {{ range: { since: string, until: string }, capability?: string, provider?: string, model?: string, userId?: string|null, billingMode?: string, costState?: string }} target
 * @returns {Record<string, string>}
 */
export function callsQueryOf({ range, capability, provider, model, userId, billingMode, costState }) {
  /** @type {Record<string, string>} */
  const query = { since: range.since, until: range.until }
  if (capability !== undefined) query.capability = capability
  if (provider !== undefined) query.provider = provider
  if (model !== undefined) query.model = model
  // user_id 为 null 的那一组是「没有用户归属的调用」，它筛不出来（后端不认 null 作为筛选值）——
  // 所以这一行不给下钻入口，而不是编一个假 user_id 传过去。
  if (userId !== undefined && userId !== null) query.user_id = userId
  if (billingMode !== undefined) query.billing_mode = billingMode
  if (costState !== undefined) query.cost_state = costState
  return query
}

/**
 * 时间轴上点中一根柱子 → 只看那一段。天粒度点开就是那一天，小时粒度点开就是那一天的那一小时。
 * @param {string} time  byTime 返回的 `time`：`YYYY-MM-DD` 或 `YYYY-MM-DD HH:00`
 * @returns {{ since: string, until: string }}
 */
export function rangeOfTimeBucket(time) {
  if (DAY_PATTERN.test(time)) {
    return { since: startOfDay(time), until: startOfDay(nextDayOf(time)) }
  }
  const hour = /^(\d{4}-\d{2}-\d{2}) (\d{2}):00$/.exec(time)
  if (!hour) throw new Error(`cost-range: 认不出的时间桶 "${time}"`)
  const since = new Date(`${hour[1]}T${hour[2]}:00:00.000Z`)
  return { since: since.toISOString(), until: new Date(since.getTime() + 3_600_000).toISOString() }
}

/** @param {string} day @param {number} offset */
function dayOffsetOf(day, offset) {
  assertDay(day)
  return new Date(Date.parse(`${day}T00:00:00.000Z`) + offset * DAY_MS).toISOString().slice(0, 10)
}

/** @param {string} day */
function nextDayOf(day) {
  return dayOffsetOf(day, 1)
}

/** @param {string} day */
function startOfDay(day) {
  return `${day}T00:00:00.000Z`
}

/** @param {string} day */
function assertDay(day) {
  if (typeof day !== 'string' || !DAY_PATTERN.test(day)) {
    throw new Error(`cost-range: 日期必须是 YYYY-MM-DD（收到 ${JSON.stringify(day)}）`)
  }
}
